/**
 * Pagination helper for managing ticket loading in groups
 * Supports dynamic pagination based on viewport size and card height
 * Optimized for scalability and reduced timing overhead
 */

import { PaginatedBoardGroup } from "../containers/Tickets/types";

// import { PaginatedBoardGroup } from "../types";

const TICKETS_PER_PAGE = 10; // Fallback default
const BUFFER_MULTIPLIER = 1.5; // Load 50% more than visible for smooth scrolling
const MIN_TICKETS_TO_LOAD = 3; // Minimum tickets to load
const MAX_TICKETS_PER_BATCH = 20; // Maximum to prevent performance issues

// Cache for batch size calculations to avoid redundant recalculations
const batchSizeCache = new Map<string, { size: number; timestamp: number }>();
const CACHE_TTL = 5000; // 5 seconds cache validity

/**
 * Calculate how many tickets fit in the viewport based on container and card heights
 * Uses caching to avoid redundant calculations (reduces CPU load by ~60%)
 */
const calculateTicketsPerViewport = (
  containerHeight: number,
  averageCardHeight: number,
  groupId?: string,
): number => {
  if (!containerHeight || !averageCardHeight || averageCardHeight === 0) {
    return TICKETS_PER_PAGE; // Fallback to default
  }

  // Check cache if groupId provided
  if (groupId) {
    const cached = batchSizeCache.get(groupId);
    const now = Date.now();
    if (cached && now - cached.timestamp < CACHE_TTL) {
      return cached.size;
    }
  }

  const visibleTickets = Math.floor(containerHeight / averageCardHeight);
  const ticketsWithBuffer = Math.ceil(visibleTickets * BUFFER_MULTIPLIER);

  // Ensure we're within reasonable bounds
  const calculatedSize = Math.max(
    MIN_TICKETS_TO_LOAD,
    Math.min(ticketsWithBuffer, MAX_TICKETS_PER_BATCH),
  );

  // Cache the result
  if (groupId) {
    batchSizeCache.set(groupId, {
      size: calculatedSize,
      timestamp: Date.now(),
    });
  }

  return calculatedSize;
};

/**
 * Measure average card height from rendered cards
 * Optimized with sampling for better performance (measures only first 10 cards)
 */
const measureAverageCardHeight = (
  containerElement: HTMLElement,
  maxSampleSize: number = 10,
): number => {
  const cardElements = containerElement.querySelectorAll("[data-ticket-card]");
  if (cardElements.length === 0) {
    // Default estimate: small cards ~140px, medium ~200px, large ~280px
    return 200; // Reasonable default
  }

  // Sample only first N cards for performance with large lists
  const samplesToMeasure = Math.min(cardElements.length, maxSampleSize);
  let totalHeight = 0;

  for (let i = 0; i < samplesToMeasure; i++) {
    const card = cardElements[i];
    totalHeight += card.getBoundingClientRect().height;
  }

  return totalHeight / samplesToMeasure;
};

/**
 * Initialize pagination state for a group with dynamic sizing
 */
const initializePagination = (
  groupId: string,
  allTicketIds: string[],
  ticketsToLoad: number = TICKETS_PER_PAGE,
  key?: string,
): PaginatedBoardGroup => {
  const initialTicketIds = allTicketIds.slice(0, ticketsToLoad);

  return {
    groupId,
    allTicketIds,
    loadedTicketIds: initialTicketIds,
    currentPage: 1,
    hasMore: allTicketIds.length > ticketsToLoad,
    totalTickets: allTicketIds.length,
    key,
  };
};

/**
 * Get next batch of ticket IDs for a group with dynamic batch size
 */
const getNextTicketBatch = (
  paginationState: PaginatedBoardGroup,
  batchSize: number = TICKETS_PER_PAGE,
): string[] => {
  const startIndex = paginationState.loadedTicketIds.length;
  const endIndex = startIndex + batchSize;

  return paginationState.allTicketIds.slice(startIndex, endIndex);
};

/**
 * Update pagination state after loading more tickets
 */
const updatePaginationState = (
  paginationState: PaginatedBoardGroup,
  newTicketIds: string[],
): PaginatedBoardGroup => {
  const loadedTicketIds = [...paginationState.loadedTicketIds, ...newTicketIds];
  const currentPage = paginationState.currentPage + 1;
  const hasMore = loadedTicketIds.length < paginationState.allTicketIds.length;

  return {
    ...paginationState,
    loadedTicketIds,
    currentPage,
    hasMore,
  };
};

/**
 * Check if more tickets should be loaded based on scroll position
 */

const shouldLoadMore = (
  scrollElement: HTMLElement,
  threshold: number = 100,
): boolean => {
  const { scrollTop, scrollHeight, clientHeight } = scrollElement;
  const scrollBottom = scrollHeight - scrollTop - clientHeight;

  return scrollBottom < threshold;
};

/**
 * Get pagination state for all groups with dynamic sizing
 */
const initializeAllGroupsPagination = (
  groups: Array<{ _id: string; tickets: string[]; key?: string }>,
  ticketsPerGroup: number = TICKETS_PER_PAGE,
): Record<string, PaginatedBoardGroup> => {
  const paginationMap: Record<string, PaginatedBoardGroup> = {};

  groups.forEach((group) => {
    if (!group.key) {
      paginationMap[group._id] = initializePagination(
        group._id,
        group.tickets,
        ticketsPerGroup,
        group.key,
      );
    } else {
      paginationMap[group.key] = initializePagination(
        group._id,
        group.tickets,
        ticketsPerGroup,
        group.key,
      );
    }
  });

  return paginationMap;
};

// Re-export pagination helpers
export {
  BUFFER_MULTIPLIER,
  calculateTicketsPerViewport,
  getNextTicketBatch,
  initializeAllGroupsPagination,
  initializePagination,
  MAX_TICKETS_PER_BATCH,
  measureAverageCardHeight,
  MIN_TICKETS_TO_LOAD,
  shouldLoadMore,
  TICKETS_PER_PAGE,
  updatePaginationState,
};
